import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import { SupabaseClient } from "@supabase/supabase-js";

export type SearchPatientRequestParams = {
  email?: string;
  phone?: string;
  name?:
    | { type: "one_word"; value: string }
    | { type: "two_words"; value: readonly [string, string] };
  birthDate?: string[];
};

export type SearchPatientRequestKey = keyof SearchPatientRequestParams;

type SearchPatientCenterTimeSlot = Pick<
  DatabaseRow<"center_time_slots">,
  "started_at"
>;

type SearchPatientCenter = Pick<DatabaseRow<"centers">, "name" | "timezone">;

type SearchPatientAppointment = Pick<DatabaseRow<"appointments">, "status"> & {
  center: SearchPatientCenter;
  center_time_slot: SearchPatientCenterTimeSlot;
};

type SearchPatientAppointmentPatientDetails = Pick<
  DatabaseRow<"appointment_patient_details">,
  "appointment_id"
> & {
  appointment: SearchPatientAppointment;
};

export type SearchPatient = Pick<
  DatabaseRow<"patients">,
  | "id"
  | "first_name"
  | "last_name"
  | "email"
  | "mobile_phone_number"
  | "additional_phone_number"
  | "date_birth"
  | "mrn"
> & {
  appointment_patient_details: SearchPatientAppointmentPatientDetails[];
};

const supabaseSearchPatientRequest = `
id,
first_name,
last_name,
email,
mobile_phone_number,
additional_phone_number,
date_birth,
mrn,
appointment_patient_details (
  appointment_id,
  appointment:appointment_id!inner (
    status,
    center:center_id (
      name,
      timezone
    ),
    center_time_slot:time_slot_id!inner (
      started_at
    )
  )
)
`;

const getSearchPatients = async (
  supabaseClient: SupabaseClient,
  params: SearchPatientRequestParams,
) => {
  if (!Object.keys(params).length) return [];

  const { email, name, phone, birthDate } = params;

  const rpcParams: {
    email_filter?: string;
    phone_filter?: string;
    name_filter?: string;
    birth_date_filter?: string[];
  } = {};

  if (email) rpcParams.email_filter = email;
  if (phone) rpcParams.phone_filter = phone;

  if (name) {
    switch (name.type) {
      case "one_word":
        rpcParams.name_filter = name.value;
        break;
      case "two_words":
        rpcParams.name_filter = name.value.join(" ");
        break;
      default:
        break;
    }
  }

  if (birthDate) rpcParams.birth_date_filter = birthDate;

  const { data: patientIds, error } = await supabaseClient
    .rpc("get_search_patient_ids", rpcParams)
    .returns<{ patient_id: string }[]>();

  if (error) throw new Error(error.message);

  if (patientIds && patientIds.length) {
    const ids = patientIds.map((patientObject) => patientObject.patient_id);

    // Merged-away patients are kept archived for history only — they must
    // never surface in search, since selecting one just redirects to the
    // surviving record. get_search_patient_ids filters them too; this second
    // filter keeps the app correct regardless of the deployed RPC version.
    const { data, error: queryError } = await supabaseClient
      .from("patients")
      .select(supabaseSearchPatientRequest)
      .in("id", ids)
      .eq("is_archived", false)
      .returns<SearchPatient[]>();

    if (queryError) throw new Error(queryError.message);

    return data;
  }

  return [];
};

export default getSearchPatients;
